import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import moment from 'moment';

import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { fetchTranslations, saveTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser, fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { completeTask, TASK_NAMES } from '../../ducks/voucherify.duck';
import { PASS_CONTINGENT_PASS_VOUCHER } from '../../util/annualPass';
import {
  stripeCancelPaymentIntent,
  transactionLineItems,
  vansiteCancelTransaction,
  vansiteCapturePaymentIntent,
  vansiteUpdateListingRating,
  vansiteWithdrawTransaction,
  voucherifyRollbackRedemption,
} from '../../util/api';
import { denormalisedEntities, denormalisedResponseEntities, updatedEntities } from '../../util/data';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import {
  getReview1Transition,
  getReview2Transition,
  TRANSITION_ACCEPT,
  TRANSITION_DECLINE,
  txIsInFirstReviewBy,
} from '../../util/transaction.default';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const CUSTOMER = 'customer';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const ACCEPT_SALE_REQUEST = 'app/TransactionPage/ACCEPT_SALE_REQUEST';
export const ACCEPT_SALE_SUCCESS = 'app/TransactionPage/ACCEPT_SALE_SUCCESS';
export const ACCEPT_SALE_ERROR = 'app/TransactionPage/ACCEPT_SALE_ERROR';

export const DECLINE_SALE_REQUEST = 'app/TransactionPage/DECLINE_SALE_REQUEST';
export const DECLINE_SALE_SUCCESS = 'app/TransactionPage/DECLINE_SALE_SUCCESS';
export const DECLINE_SALE_ERROR = 'app/TransactionPage/DECLINE_SALE_ERROR';

export const CANCEL_SALE_REQUEST = 'app/TransactionPage/CANCEL_SALE_REQUEST';
export const CANCEL_SALE_SUCCESS = 'app/TransactionPage/CANCEL_SALE_SUCCESS';
export const CANCEL_SALE_ERROR = 'app/TransactionPage/CANCEL_SALE_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/TransactionPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/TransactionPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const WITHDRAW_PRE_AUTHORIZED_TRANSACTION_REQUEST =
  'app/TransactionPage/WITHDRAW_PRE_AUTHORIZED_TRANSACTION_REQUEST';
export const WITHDRAW_PRE_AUTHORIZED_TRANSACTION_SUCCESS =
  'app/TransactionPage/WITHDRAW_PRE_AUTHORIZED_TRANSACTION_SUCCESS';
export const WITHDRAW_PRE_AUTHORIZED_TRANSACTION_ERROR =
  'app/TransactionPage/WITHDRAW_PRE_AUTHORIZED_TRANSACTION_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.TransactionPage} */
const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  acceptInProgress: false,
  acceptSaleError: null,
  declineInProgress: false,
  declineSaleError: null,
  cancelInProgress: false,
  cancelSaleError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: [],
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  withdrawInProgress: false,
  withdrawError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter((aEntity) => !b.find((bEntity) => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

/**
 *
 * @param state
 * @param action
 */
export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case ACCEPT_SALE_REQUEST:
      return { ...state, acceptInProgress: true, acceptSaleError: null, declineSaleError: null };
    case ACCEPT_SALE_SUCCESS:
      return { ...state, acceptInProgress: false };
    case ACCEPT_SALE_ERROR:
      return { ...state, acceptInProgress: false, acceptSaleError: payload };

    case DECLINE_SALE_REQUEST:
      return { ...state, declineInProgress: true, declineSaleError: null, acceptSaleError: null };
    case DECLINE_SALE_SUCCESS:
      return { ...state, declineInProgress: false };
    case DECLINE_SALE_ERROR:
      return { ...state, declineInProgress: false, declineSaleError: payload };

    case CANCEL_SALE_REQUEST:
      return { ...state, cancelInProgress: true, cancelSaleError: null };
    case CANCEL_SALE_SUCCESS:
      return { ...state, cancelInProgress: false };
    case CANCEL_SALE_ERROR:
      return { ...state, cancelInProgress: false, cancelSaleError: payload };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_TIME_SLOTS_REQUEST:
      return { ...state, fetchTimeSlotsError: null };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, timeSlots: payload };
    case FETCH_TIME_SLOTS_ERROR:
      return { ...state, fetchTimeSlotsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case WITHDRAW_PRE_AUTHORIZED_TRANSACTION_REQUEST:
      return { ...state, withdrawInProgress: true, withdrawError: null };
    case WITHDRAW_PRE_AUTHORIZED_TRANSACTION_SUCCESS:
      return { ...state, withdrawInProgress: false };
    case WITHDRAW_PRE_AUTHORIZED_TRANSACTION_ERROR:
      return { ...state, withdrawInProgress: false, withdrawError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const acceptOrDeclineInProgress = (state) => {
  return state.TransactionPage.acceptInProgress || state.TransactionPage.declineInProgress;
};

export const cancelInProgress = (state) => {
  return state.TransactionPage.cancelInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = (response) => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = (e) => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const acceptSaleRequest = () => ({ type: ACCEPT_SALE_REQUEST });
const acceptSaleSuccess = () => ({ type: ACCEPT_SALE_SUCCESS });
const acceptSaleError = (e) => ({ type: ACCEPT_SALE_ERROR, error: true, payload: e });

const declineSaleRequest = () => ({ type: DECLINE_SALE_REQUEST });
const declineSaleSuccess = () => ({ type: DECLINE_SALE_SUCCESS });
const declineSaleError = (e) => ({ type: DECLINE_SALE_ERROR, error: true, payload: e });

const cancelSaleRequest = () => ({ type: CANCEL_SALE_REQUEST });
const cancelSaleSuccess = () => ({ type: CANCEL_SALE_SUCCESS });
const cancelSaleError = (e) => ({ type: CANCEL_SALE_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = (e) => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = (e) => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = (e) => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

const fetchTimeSlotsRequest = () => ({ type: FETCH_TIME_SLOTS_REQUEST });
const fetchTimeSlotsSuccess = (timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: timeSlots,
});
const fetchTimeSlotsError = (e) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: e,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = (lineItems) => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = (error) => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const withdrawPreAuthorizedTransactionRequest = () => ({
  type: WITHDRAW_PRE_AUTHORIZED_TRANSACTION_REQUEST,
});
export const withdrawPreAuthorizedTransactionSuccess = () => ({
  type: WITHDRAW_PRE_AUTHORIZED_TRANSACTION_SUCCESS,
});
export const withdrawPreAuthorizedTransactionError = (error) => ({
  type: WITHDRAW_PRE_AUTHORIZED_TRANSACTION_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

const listingRelationship = (txResponse) => {
  return txResponse.data.data.relationships.listing.data;
};

export const fetchTransaction = (id, txRole, locale) => async (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());
  try {
    const txResponse = await sdk.transactions.show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.author',
          'listing.author.profileImage',
          'listing.images',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...IMAGE_VARIANTS,
      },
      { expand: true },
    );
    const listingId = listingRelationship(txResponse).id;
    const entities = updatedEntities({}, txResponse.data);
    const listingRef = { id: listingId, type: 'listing' };
    const transactionRef = { id, type: 'transaction' };
    const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
    const transaction = denormalised[1];

    // Fetch time slots for transactions that are in enquired state
    const canFetchTimeslots = txRole === 'customer' && config.enableAvailability && transaction;

    if (canFetchTimeslots) {
      dispatch(fetchTimeSlots(listingId));
    }

    dispatch(
      fetchTranslations(
        {
          transaction: { ids: [txResponse.data.data.id.uuid] },
          listing: { ids: [listingId?.uuid] },
        },
        locale,
      ),
    );
    dispatch(addMarketplaceEntities(txResponse));
    dispatch(fetchTransactionSuccess(txResponse));
    return txResponse;
  } catch (e) {
    logError(e, 'fetch-transaction-failed');
    dispatch(fetchTransactionError(storableError(e)));
    throw e;
  }
};

export const acceptSale = (id) => async (dispatch, getState, sdk) => {
  if (acceptOrDeclineInProgress(getState())) {
    return Promise.reject(new Error('Accept or decline already in progress'));
  }
  dispatch(acceptSaleRequest());

  try {
    const apiResponse = await vansiteCapturePaymentIntent({ transactionId: id.uuid });
    dispatch(addMarketplaceEntities(apiResponse));
    dispatch(acceptSaleSuccess());
    dispatch(fetchCurrentUserNotifications());
    return apiResponse;
  } catch (e) {
    dispatch(acceptSaleError(storableError(e)));
    logError(e, 'accept-sale-failed', {
      txId: id,
      transition: TRANSITION_ACCEPT,
    });
    throw e;
  }
};

export const declineSale = (id) => async (dispatch, getState, sdk) => {
  if (acceptOrDeclineInProgress(getState())) {
    return Promise.reject(new Error('Accept or decline already in progress'));
  }
  dispatch(declineSaleRequest());

  try {
    let apiResponse = await sdk.transactions.show(
      { id: id.uuid, include: ['listing'] },
      { expand: true },
    );
    const transaction = apiResponse.data.data;
    const hasPaymentIntent =
      transaction.attributes?.protectedData?.stripePaymentIntents?.default?.stripePaymentIntentId;
    if (hasPaymentIntent) {
      stripeCancelPaymentIntent({ paymentIntentId: hasPaymentIntent });
    }
    const hasRedemption =
      transaction.attributes.metadata.redemption ||
      transaction.attributes.metadata.voucher?.redemption ||
      transaction.attributes.metadata.annualPassVoucher?.redemption;
    if (hasRedemption) {
      voucherifyRollbackRedemption({ id: hasRedemption });
    }

    apiResponse = await sdk.transactions.transition(
      {
        id: id.uuid,
        transition: TRANSITION_DECLINE,
        params: {
          metadata: {
            ...transaction.attributes.metadata,
            customerNotification: false,
          },
        },
      },
      { expand: true },
    );
    dispatch(addMarketplaceEntities(apiResponse));
    dispatch(declineSaleSuccess());
    dispatch(fetchCurrentUserNotifications());
    return apiResponse;
  } catch (e) {
    dispatch(declineSaleError(storableError(e)));
    logError(e, 'reject-sale-failed', {
      txId: id.uuid,
      transition: TRANSITION_DECLINE,
    });
    throw e;
  }
};

/**
 * @type {(id: UUID, VanSite.UserRole, VanSite.CancellationReason ) => AppThunkAction}
 */
export const cancelSale = (id, userRole, reason) => async (dispatch, getState, sdk) => {
  if (cancelInProgress(getState())) {
    return Promise.reject(new Error('Cancel already in progress'));
  }
  dispatch(cancelSaleRequest());
  try {
    // transition for sharetribe
    const getTransactionAttributes = (transaction) => transaction?.attributes || {};
    const getProtectedData = (attributes) => attributes.protectedData || {};
    const getMetadata = (attributes) => attributes.metadata || {};

    let sharetribeApiResponse = await sdk.transactions.show({ id: id.uuid }, { expand: true });
    let transaction = sharetribeApiResponse.data.data;

    sharetribeApiResponse = await vansiteCancelTransaction({ transactionId: id.uuid, userRole, reason });
    transaction = sharetribeApiResponse.data.data;

    const attributes = getTransactionAttributes(transaction);
    const protectedData = getProtectedData(attributes);
    const metadata = getMetadata(attributes);

    const hasPaymentIntent = protectedData.stripePaymentIntents?.default?.stripePaymentIntentId;
    const hasRedemption = metadata.redemption || metadata.voucher?.redemption;
    const hasUsedGiftCard = metadata.voucher?.giftCard;
    const isContingentVoucher = metadata.voucher?.type === PASS_CONTINGENT_PASS_VOUCHER;
    if (hasPaymentIntent) {
      // cancel stripe payment intent when a payment intent exists
      stripeCancelPaymentIntent({ paymentIntentId: hasPaymentIntent });
    }
    if (
      (hasRedemption && !hasUsedGiftCard && isContingentVoucher) ||
      (hasRedemption && !hasUsedGiftCard && userRole === 'customer') ||
      (hasRedemption && hasUsedGiftCard && userRole === 'provider')
    ) {
      // cancel voucherify redemption when a redemption exists
      voucherifyRollbackRedemption({ id: hasRedemption });
    }
    // we need to make a payment intent and a payout when the customer cancels before a payment have been done
    dispatch(addMarketplaceEntities(sharetribeApiResponse));
    dispatch(cancelSaleSuccess());
    dispatch(fetchCurrentUserNotifications());
    return sharetribeApiResponse;
  } catch (e) {
    dispatch(cancelSaleError(storableError(e)));
    logError(e, 'cancel-sale-failed', {
      txId: id.uuid,
      transition:
        userRole === 'provider'
          ? 'TRANSITION_PROVIDER_CANCEL'
          : userRole === 'customer'
            ? 'TRANSITION_CUSTOMER_CANCEL'
            : 'TRANSITION_CANCEL',
    });
  }
};

const fetchMessages = (txId, page) => (dispatch, getState, sdk) => {
  const paging = { page, per_page: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...IMAGE_VARIANTS,
      ...paging,
    })
    .then((response) => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch((e) => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId) => (dispatch, getState) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage));
};

export const sendMessage = (txId, message) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then((response) => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch((e) => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

const REVIEW_TX_INCLUDES = ['listing', 'reviews', 'reviews.author', 'reviews.subject'];
const IMAGE_VARIANTS = {
  'fields.image': [
    // Profile images
    'variants.square-small',
    'variants.square-small2x',

    // Listing images:
    'variants.landscape-crop',
    'variants.landscape-crop2x',
  ],
};

// If other party has already sent a review, we need to make transition to
// TRANSITION_REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = async (id, params, role, currentUser, dispatch, sdk) => {
  const transition = getReview2Transition(role === CUSTOMER);

  const include = REVIEW_TX_INCLUDES;

  try {
    const response = await sdk.transactions.transition(
      { id, transition, params },
      {
        expand: true,
        include,
        ...IMAGE_VARIANTS,
      },
    );
    const reviews = response.data.included.filter((i) => i.type === 'review');
    for (const review of reviews) {
      dispatch(
        saveTranslations({
          type: 'review',
          id: review.id.uuid,
          content: { content: review.attributes.content },
        }),
      );
    }
    dispatch(addMarketplaceEntities(response));
    dispatch(sendReviewSuccess());
    const listing = response.data.included.filter((include) => include.type === 'listing')[0];
    vansiteUpdateListingRating({ listingId: listing.id.uuid });
    dispatch(completeTask(TASK_NAMES.REVIEW_LEFT));
    return response;
  } catch (e) {
    dispatch(sendReviewError(storableError(e)));

    // Rethrow so the page can track whether the sending failed, and
    // keep the message in the form for a retry.
    throw e;
  }
};

// If other party has not yet sent a review, we need to make transition to
// TRANSITION_REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = async (id, params, role, currentUser, dispatch, sdk) => {
  const transition = getReview1Transition(role === CUSTOMER);
  const include = REVIEW_TX_INCLUDES;

  try {
    const response = await sdk.transactions.transition(
      { id, transition, params },
      {
        expand: true,
        include,
        ...IMAGE_VARIANTS,
      },
    );
    dispatch(addMarketplaceEntities(response));
    dispatch(sendReviewSuccess());
    dispatch(completeTask(TASK_NAMES.REVIEW_LEFT));
    return response;
  } catch (e) {
    // If transaction transition is invalid, lets try another endpoint.
    if (isTransactionsTransitionInvalidTransition(e)) {
      return sendReviewAsSecond(id, params, role, dispatch, sdk);
    } else {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    }
  }
};

export const sendReview =
  (role, tx, reviewRating, reviewContent) => async (dispatch, getState, sdk) => {
    const params = { reviewRating, reviewContent };

    const txStateOtherPartyFirst = txIsInFirstReviewBy(tx, role !== CUSTOMER);

    const { currentUser } = getState().user;
    dispatch(sendReviewRequest());

    return txStateOtherPartyFirst
      ? await sendReviewAsSecond(tx.id, params, role, currentUser, dispatch, sdk)
      : await sendReviewAsFirst(tx.id, params, role, currentUser, dispatch, sdk);
  };

const isNonEmpty = (value) => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

const timeSlotsRequest = (params) => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then((response) => {
    return denormalisedResponseEntities(response);
  });
};

const fetchTimeSlots = (listingId) => (dispatch) => {
  dispatch(fetchTimeSlotsRequest);

  // Time slots can be fetched for 90 days at a time,
  // for at most 180 days from now. If max number of bookable
  // day exceeds 90, a second request is made.

  const maxTimeSlots = 90;
  // booking range: today + bookable days -1
  const bookingRange = config.dayCountAvailableForBooking - 1;
  const timeSlotsRange = Math.min(bookingRange, maxTimeSlots);

  const start = moment().utc().startOf('day').toDate();
  const end = moment().utc().startOf('day').add(timeSlotsRange, 'days').toDate();
  const params = { listingId, start, end };

  return dispatch(timeSlotsRequest(params))
    .then((timeSlots) => {
      const secondRequest = bookingRange > maxTimeSlots;

      if (secondRequest) {
        const secondRange = Math.min(maxTimeSlots, bookingRange - maxTimeSlots);
        const secondParams = {
          listingId,
          start: end,
          end: moment(end).add(secondRange, 'days').toDate(),
        };

        return dispatch(timeSlotsRequest(secondParams)).then((secondBatch) => {
          const combined = timeSlots.concat(secondBatch);
          dispatch(fetchTimeSlotsSuccess(combined));
        });
      } else {
        dispatch(fetchTimeSlotsSuccess(timeSlots));
      }
    })
    .catch((e) => {
      dispatch(fetchTimeSlotsError(storableError(e)));
    });
};

export const fetchTransactionLineItems =
  ({ bookingData, listingId, isOwnListing }) =>
    (dispatch) => {
      dispatch(fetchLineItemsRequest());
      transactionLineItems({ bookingData, listingId, isOwnListing })
        .then((response) => {
          const lineItems = response.data;
          dispatch(fetchLineItemsSuccess(lineItems));
        })
        .catch((e) => {
          dispatch(fetchLineItemsError(storableError(e)));
          logError(e, 'fetching-line-items-failed', {
            listingId: listingId.uuid,
            bookingData: bookingData,
          });
        });
    };

export const unStuckAutoConfirmPaymentBooking = (id) => async (dispatch) => {
  try {
    const response = await vansiteCapturePaymentIntent({ transactionId: id.uuid });
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchTransactionSuccess(response));
    return response;
  } catch (e) {
    logError(e, 'unstuck-auto-accept-booking-failed', {
      txId: id,
    });
    throw e;
  }
};

export const withdrawPreAuthorizedTransaction = (id) => async (dispatch, _, sdk) => {
  try {
    dispatch(withdrawPreAuthorizedTransactionRequest());
    await sdk.transactions.transition({
      id: id.uuid,
      transition: 'transition/withdraw',
      params: {},
    });
    const response = await vansiteWithdrawTransaction({ transactionId: id.uuid });
    dispatch(addMarketplaceEntities(response));
    const transaction = response.data.data;
    const stripePaymentIntents = transaction.attributes.protectedData.stripePaymentIntents;
    if (stripePaymentIntents && stripePaymentIntents.default) {
      await stripeCancelPaymentIntent({
        paymentIntentId: stripePaymentIntents.default.stripePaymentIntentId,
      });
    }
    dispatch(withdrawPreAuthorizedTransactionSuccess());
  } catch (e) {
    dispatch(withdrawPreAuthorizedTransactionError(storableError(e)));
    logError(e, 'withdraw-pre-authorized-transaction-failed', {
      txId: id,
    });
    throw e;
  }
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, match) => (dispatch, getState) => {
  const locale = getLocaleFromPath(match.path);
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  // Sale / order (i.e. transaction entity in API)
  return Promise.all([
    dispatch(fetchTransaction(txId, txRole, locale)),
    dispatch(fetchMessages(txId, 1)),
    dispatch(fetchCurrentUser()).then(() => dispatch(fetchStripeAccount())),
  ]);
};
